import type { GoogleTagManager } from '@exam-room/utils';
import * as Sentry from '@sentry/react';
import { LogLevel } from '@shared/logger';

function resolveString(env: string | undefined, defaultValue: string): string {
  if (env === undefined || env === null) {
    return defaultValue;
  }
  return env;
}

function resolveInteger(env: string | undefined, defaultValue: number): number {
  if (env === undefined || env === null) {
    return defaultValue;
  }

  const parsed = parseInt(env);
  if (isNaN(parsed)) {
    throw new Error(`Cannot parse ${env} as integer`);
  }
  return parsed;
}

function resolveBoolean(env: string | undefined, defaultValue: boolean): boolean {
  if (env === undefined || env === null) {
    return defaultValue;
  }

  switch (env.toUpperCase()) {
    case 'TRUE':
      return true;
    case 'FALSE':
      return false;
    default:
      throw new Error(`Cannot parse ${env} as boolean`);
  }
}

function resolveObject(env: string | undefined, defaultValue: object) {
  if (env === undefined || env === null) {
    return defaultValue;
  }

  try {
    return JSON.parse(env);
  } catch (error) {
    console.error('resolveObject error:', error);
    Sentry.captureException(error, {
      tags: {
        feature: 'config-parsing',
      },
      extra: {
        env,
      },
    });
    return defaultValue;
  }
}

function resolveArray<T>(env: string | undefined, defaultValue: T[]): T[] {
  if (env === undefined || env === null) {
    return defaultValue;
  }

  try {
    const parsed = JSON.parse(env);
    if (!Array.isArray(parsed)) {
      throw new Error(`Parsed value is not an array: ${env}`);
    }
    return parsed as T[];
  } catch (error) {
    console.error('resolveArray error:', error);
    Sentry.captureException(error, {
      tags: {
        feature: 'config-parsing',
      },
      extra: {
        env,
      },
    });
    return defaultValue;
  }
}

const defaultConfig = {
  appState: 'READY',
  examRoomMode: resolveString(import.meta.env.VITE_EXAM_ROOM_MODE, 'exam'),
  graphqlURL: resolveString(import.meta.env.VITE_GRAPHQL_URL, ''),
  credentialAuth: {
    loginPath: resolveString(import.meta.env.VITE_LOGIN_PATH, ''),
  },
  examRoom: {
    idleTime: resolveInteger(import.meta.env.VITE_IDLE_TIME, 100),
    readingAndWritingDirectionsContent: resolveString(
      import.meta.env.VITE_READING_AND_WRITING_DIRECTIONS_CONTENT,
      '',
    ),
    mathDirectionsContent: resolveString(import.meta.env.VITE_MATH_DIRECTIONS_CONTENT, ''),
    mathHelperArticleContent: resolveString(import.meta.env.VITE_MATH_HELPER_ARTICLE_CONTENT, ''),
  },
  examEvent: {
    requiredEventIds: resolveObject(import.meta.env.VITE_REQUIRED_EVENT_IDS, []),
  },
  myScores: {
    estimateScoreURL: resolveString(import.meta.env.VITE_ESTIMATE_SCORE_URL, ''),
  },
  cookie: {
    examSessionCookieName: resolveString(
      import.meta.env.VITE_APP_EXAM_SESSION_COOKIE_NAME,
      'examSession',
    ),
  },
  termsAndConditions: {
    url: resolveString(
      import.meta.env.VITE_TERMS_AND_CONDITIONS_URL,
      'https://storage.googleapis.com/exam_public_assets/documents/20240214_terms.md',
    ),
  },
  analytics: {
    googleAnalyticsIds: resolveArray<string>(import.meta.env.VITE_GOOGLE_ANALYTICS_ID, []),
    googleTagManagers: resolveArray<GoogleTagManager>(import.meta.env.VITE_GOOGLE_TAG_MANAGERS, []),
  },
  safeExamBrowser: {
    quitLink: resolveString(
      import.meta.env.VITE_APP_SEB_QUIT_LINK,
      'https://sete-seb-exit-dev.web.app',
    ),
  },
  logLevel: resolveString(import.meta.env.VITE_LOG_LEVEL, LogLevel.INFO),
  heartbeat: {
    url: resolveString(import.meta.env.VITE_HEARTBEAT_URL, ''),
    intervalMs: resolveInteger(import.meta.env.VITE_HEARTBEAT_INTERVAL_MS, 60000),
  },
  submitted: {
    continueButtonCountdownTimeSeconds: resolveInteger(
      import.meta.env.VITE_SUBMITTED_CONTINUE_BUTTON_COUNTDOWN_TIME_SECONDS,
      30,
    ),
  },
};

export default defaultConfig;
